/**
 * Creates update loops.
 * @example
 * const loop = new Loop(handler);
 * loop.run();
 */
class Loop {

    /**
     * Stores the handler to run with the loop.
     * @type {Function}
     * @private
     */
    $handler;

    /**
     * Stores the identifier of the last requestAnimationFrame call.
     * @type {number}
     * @private
     */
    $identifier;

    /**
     * Stores the time value of the last frame call.
     * @type {number}
     * @private
     */
    $lasttime;

    /**
     * Stores the accumulated latency time since the loop started.
     * @type {number}
     * @private
     */
    $latencytime;

    /**
     * Stores the global scope used.
     * @type {typeof globalThis}
     * @private
     */
    $scope;

    /**
     * Creates a new update loop.
     * @param {Function} $handler The handler to run with the loop.
     * @param {typeof globalThis} $scope The global scope to use.
     */
    constructor($handler, $scope = window) {

        this.$handler = $handler;
        this.$latencytime = 0;
        this.$scope = $scope;
    }

    /**
     * Destroys the loop.
     * @public
     */
    destroy() {

        if (typeof this.$identifier !== 'undefined') {

            this.$scope.cancelAnimationFrame(this.$identifier);
        }
    }

    /**
     * Runs the loop.
     * @param {number} [$framerate] The framerate to run the loop (in fps).
     * @public
     */
    run($framerate = 60) {

        const currenttime = performance.now();

        if (typeof this.$lasttime !== 'undefined') {

            this.$latencytime += currenttime - this.$lasttime;

            const timeframe = 1000 / $framerate;

            if (this.$latencytime >= timeframe) {

                this.$latencytime -= timeframe;

                this.$handler(timeframe);
            }
        }

        this.$identifier = this.$scope.requestAnimationFrame(this.run.bind(this, $framerate));

        this.$lasttime = currenttime;
    }
}

export {

    Loop
};

export default Loop;
